import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

/**
 * PageRenderer — Fetches widgets from the API for a given page key
 * and renders them using the same widget previews as the admin editor.
 * Pages pass their `pageKey` (e.g. 'home', 'about', 'contact', 'find_surveyor')
 * and optionally inject functional sections between widgets via `children`.
 */

// ─── Individual Widget Renderers ───

function HeroWidget({ d, s }) {
  const align = d.textAlign || 'left';
  const isCenter = align === 'center';
  const isRight = align === 'right';
  const overlayColor = d.overlayColor || '#000000';
  const overlayOpacity = d.overlayOpacity ?? 0.5;
  const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16); return `rgba(${r},${g},${b},${a})`; };

  return (
    <section
      className="relative overflow-hidden"
      style={{
        backgroundColor: d.bgColor || '#0B5D1E',
        backgroundImage: d.bgImage ? `url(${d.bgImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {d.overlay && (
        <div style={{
          position: 'absolute', inset: 0,
          background: isCenter
            ? hexToRgba(overlayColor, overlayOpacity)
            : isRight
            ? `linear-gradient(to left, ${hexToRgba(overlayColor, overlayOpacity)} 40%, transparent 100%)`
            : `linear-gradient(to right, ${hexToRgba(overlayColor, overlayOpacity)} 40%, transparent 100%)`,
        }} />
      )}
      <div className="relative" style={{ paddingTop: 80, paddingBottom: 80, zIndex: 1 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isCenter ? 'center' : isRight ? 'flex-end' : 'flex-start', textAlign: align }}>
          <div style={{ maxWidth: isCenter ? 768 : 640 }}>
            {d.subheading && !d.heading && (
              <p style={{ color: d.subheadingColor || d.textColor || '#C8A951', fontSize: d.subheadingSize || '0.875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>{d.subheading}</p>
            )}
            <h1 style={{ fontSize: d.headingSize || 'clamp(2rem, 5vw, 3rem)', fontWeight: d.headingWeight || 700, color: d.headingColor || d.textColor || '#fff', marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>{d.heading}</h1>
            {d.subheading && d.heading && (
              <p style={{ color: d.subheadingColor || (d.textColor ? hexToRgba(d.textColor, 0.7) : 'rgba(255,255,255,0.7)'), fontSize: d.subheadingSize || '1.125rem', fontWeight: d.subheadingWeight || 400 }}>{d.subheading}</p>
            )}
            {d.buttonText && (
              <div style={{ marginTop: 32 }}>
                <Link
                  to={d.buttonUrl || '#'}
                  className="inline-flex items-center gap-2 bg-[#C8A951] hover:bg-[#B89A42] text-[#062E10] px-8 py-3.5 rounded-lg text-sm font-bold tracking-wide transition shadow-lg"
                >
                  {d.buttonText}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function HeadingWidget({ d, s }) {
  const Tag = d.tag || 'h2';
  const defaultSize = Tag === 'h1' ? '2.5rem' : Tag === 'h2' ? '2rem' : '1.5rem';
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4">
        <Tag
          className={`${d.align === 'center' ? 'text-center section-heading-center' : 'section-heading'}`}
          style={{ fontSize: d.textSize || defaultSize, fontWeight: d.textWeight || 700, color: d.textColor || '#111827' }}
        >
          {d.text}
        </Tag>
      </div>
    </section>
  );
}

function TextWidget({ d, s }) {
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4">
        <div
          className="prose prose-gray max-w-none leading-relaxed"
          style={{ textAlign: d.align || 'left' }}
          dangerouslySetInnerHTML={{ __html: d.html || '' }}
        />
      </div>
    </section>
  );
}

function ImageWidget({ d, s }) {
  if (!d.url) return null;
  const img = <img src={d.url} alt={d.alt || ''} style={{ maxWidth: d.width || '100%', height: 'auto' }} className="rounded-xl" />;
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4 text-center">
        {d.link ? <a href={d.link}>{img}</a> : img}
      </div>
    </section>
  );
}

function ButtonWidget({ d, s }) {
  const vars = {
    primary: 'bg-[#0B5D1E] hover:bg-[#062E10] text-white',
    secondary: 'bg-[#C8A951] hover:bg-[#B89A42] text-[#062E10]',
    outline: 'border-2 border-[#0B5D1E] text-[#0B5D1E] hover:bg-[#0B5D1E]/5',
    dark: 'bg-[#062E10] hover:bg-black text-white',
  };
  const sizes = { sm: 'px-4 py-2 text-xs', md: 'px-6 py-3 text-sm', lg: 'px-8 py-4 text-base' };
  return (
    <section style={wrapStyle(s)}>
      <div className={`max-w-7xl mx-auto px-4 ${d.align === 'center' ? 'text-center' : d.align === 'right' ? 'text-right' : ''}`}>
        <Link
          to={d.url || '#'}
          className={`inline-block rounded-lg font-bold tracking-wide transition shadow-md ${vars[d.variant] || vars.primary} ${sizes[d.size] || sizes.md}`}
        >
          {d.text || 'Button'}
        </Link>
      </div>
    </section>
  );
}

function SpacerWidget({ d }) {
  return <div style={{ height: d.height || 40 }} />;
}

function DividerWidget({ d, s }) {
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4">
        <hr style={{ border: 'none', borderTop: `2px ${d.style || 'solid'} ${d.color || '#e5e7eb'}` }} />
      </div>
    </section>
  );
}

function VideoWidget({ d, s }) {
  let src = d.url || '';
  if (src.includes('youtube.com/watch')) src = src.replace('watch?v=', 'embed/');
  else if (src.includes('youtu.be/')) src = src.replace('youtu.be/', 'www.youtube.com/embed/');
  else if (src.includes('vimeo.com/')) src = src.replace('vimeo.com/', 'player.vimeo.com/video/');
  if (!src) return null;
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4">
        <iframe title="video" src={src} className="w-full rounded-xl" style={{ aspectRatio: '16/9', border: 0 }} allowFullScreen />
      </div>
    </section>
  );
}

function CardsWidget({ d, s }) {
  const cols = { 1: 'grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' };
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4">
        <div className={`grid grid-cols-1 ${cols[d.columns] || cols[3]} gap-6`}>
          {(d.items || []).map((c, i) => (
            <div key={i} className="card-hover bg-white rounded-xl p-7 border border-gray-100 shadow-sm">
              {c.icon && <span className="text-3xl mb-4 block">{c.icon}</span>}
              <h3 style={{ fontSize: c.titleSize || '1.125rem', fontWeight: c.titleWeight || 700, color: c.titleColor || '#111827', fontFamily: 'Inter, sans-serif', marginBottom: 8 }}>{c.title}</h3>
              <p style={{ fontSize: c.descSize || '0.875rem', fontWeight: c.descWeight || 400, color: c.descColor || '#6b7280', lineHeight: 1.6 }}>{c.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTAWidget({ d, s }) {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        backgroundColor: d.bgColor || '#0B5D1E',
        paddingTop: s.paddingTop || 48,
        paddingBottom: s.paddingBottom || 48,
      }}
    >
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 style={{ fontSize: d.headingSize || '2.25rem', fontWeight: d.headingWeight || 700, color: d.headingColor || '#ffffff', marginBottom: 16 }}>{d.heading}</h2>
        <p style={{ fontSize: d.descSize || '1.125rem', fontWeight: d.descWeight || 400, color: d.descColor || 'rgba(255,255,255,0.7)', marginBottom: 32 }}>{d.description}</p>
        {d.buttonText && (
          <Link
            to={d.buttonUrl || '#'}
            className="inline-flex items-center gap-2 bg-[#C8A951] hover:bg-[#B89A42] text-[#062E10] px-8 py-3.5 rounded-lg text-sm font-bold tracking-wide transition shadow-lg"
          >
            {d.buttonText}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </Link>
        )}
      </div>
    </section>
  );
}

function IconBoxWidget({ d, s }) {
  return (
    <section style={wrapStyle(s)}>
      <div className={`max-w-7xl mx-auto px-4 ${d.align === 'center' ? 'text-center' : ''}`}>
        <div className="text-4xl mb-3">{d.icon}</div>
        <h3 style={{ fontSize: d.titleSize || '1.25rem', fontWeight: d.titleWeight || 700, color: d.titleColor || '#111827', marginBottom: 8 }}>{d.title}</h3>
        <p style={{ fontSize: d.descSize || '1rem', fontWeight: d.descWeight || 400, color: d.descColor || '#6b7280', lineHeight: 1.6 }}>{d.description}</p>
      </div>
    </section>
  );
}

function TestimonialWidget({ d, s }) {
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-3xl mx-auto px-4 text-center">
        <div className="bg-[#F8FAFB] rounded-2xl p-10 border border-gray-100">
          <span className="text-4xl text-[#C8A951]">"</span>
          <p style={{ fontSize: d.quoteSize || '1.125rem', fontWeight: d.quoteWeight || 400, color: d.quoteColor || '#374151', fontStyle: 'italic', lineHeight: 1.7, marginBottom: 24 }}>{d.quote}</p>
          <div style={{ fontSize: d.authorSize || '1rem', fontWeight: d.authorWeight || 700, color: d.authorColor || '#111827' }}>{d.author}</div>
          <div style={{ fontSize: d.roleSize || '0.875rem', fontWeight: d.roleWeight || 400, color: d.roleColor || '#6b7280' }}>{d.role}</div>
        </div>
      </div>
    </section>
  );
}

function CounterWidget({ d, s }) {
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {(d.items || []).map((c, i) => (
            <div key={i}>
              <div style={{ fontSize: c.valueSize || '2.25rem', fontWeight: c.valueWeight || 700, color: c.valueColor || '#0B5D1E', fontFamily: "'Playfair Display',serif" }}>
                {c.suffix === 'FCT' ? 'FCT' : <>{c.value}{c.suffix}</>}
              </div>
              <div style={{ fontSize: c.labelSize || '0.875rem', fontWeight: c.labelWeight || 400, color: c.labelColor || '#6b7280', marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AccordionWidget({ d, s }) {
  const [open, setOpen] = useState(null);
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-3xl mx-auto px-4">
        {(d.items || []).map((it, i) => (
          <div key={i} className="border-b border-gray-200">
            <button onClick={() => setOpen(open === i ? null : i)}
              className="w-full py-4 flex justify-between items-center text-left hover:text-[#0B5D1E] transition"
              style={{ fontSize: it.titleSize || '1rem', fontWeight: it.titleWeight || 600, color: it.titleColor || '#111827' }}>
              {it.title}
              <svg className={`w-5 h-5 transition-transform ${open === i ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {open === i && <div className="pb-4 leading-relaxed" style={{ fontSize: it.contentSize || '0.875rem', fontWeight: it.contentWeight || 400, color: it.contentColor || '#6b7280' }}>{it.content}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function HTMLWidget({ d, s }) {
  return (
    <section style={wrapStyle(s)}>
      <div className="max-w-7xl mx-auto px-4" dangerouslySetInnerHTML={{ __html: d.code || '' }} />
    </section>
  );
}

function SliderWidget({ d, s }) {
  const slides = d.slides || [];
  const [idx, setIdx] = React.useState(0);
  const len = slides.length;
  const align = d.textAlign || 'center';
  const isCenter = align === 'center';
  const isRight = align === 'right';
  const showOverlay = d.overlay !== false;
  const overlayColor = d.overlayColor || '#000000';
  const overlayOpacity = d.overlayOpacity ?? 0.5;
  const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16); return `rgba(${r},${g},${b},${a})`; };

  React.useEffect(() => {
    if (d.autoplay === false || len < 2) return;
    const t = setInterval(() => setIdx(p => (p + 1) % len), d.interval || 6000);
    return () => clearInterval(t);
  }, [len, d.autoplay, d.interval]);

  if (!len) return null;
  const sl = slides[idx] || {};

  return (
    <section style={wrapStyle(s)} className="relative overflow-hidden">
      <div className="relative" style={{ minHeight: 520, background: '#062E10' }}>
        {sl.image && (
          <img
            key={idx}
            src={sl.image}
            alt={sl.title || ''}
            className="animate-fadeIn"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {showOverlay && (
          <div style={{
            position: 'absolute', inset: 0,
            background: isCenter
              ? hexToRgba(overlayColor, overlayOpacity)
              : isRight
              ? `linear-gradient(to left, ${hexToRgba(overlayColor, overlayOpacity)} 40%, transparent 100%)`
              : `linear-gradient(to right, ${hexToRgba(overlayColor, overlayOpacity)} 40%, transparent 100%)`,
          }} />
        )}

        <div
          style={{
            position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column',
            alignItems: isCenter ? 'center' : isRight ? 'flex-end' : 'flex-start',
            justifyContent: 'center', textAlign: align,
            color: '#fff', padding: '0 48px', minHeight: 520,
          }}
        >
          <div style={{ maxWidth: isCenter ? 768 : 640 }}>
            {sl.subtitle && (
              <p style={{ fontSize: sl.subtitleSize || 'clamp(0.75rem,1.5vw,1rem)', fontWeight: sl.subtitleWeight || 600, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12, color: sl.subtitleColor || sl.textColor || '#E8D48B' }}>{sl.subtitle}</p>
            )}
            <h1 style={{ fontSize: sl.titleSize || 'clamp(1.8rem,5vw,3.75rem)', fontWeight: sl.titleWeight || 700, marginBottom: 16, fontFamily: "'Playfair Display',serif", color: sl.titleColor || sl.textColor || '#fff' }}>
              {sl.title}
            </h1>
            {sl.description && (
              <p style={{ fontSize: sl.descriptionSize || 'clamp(1rem,2vw,1.25rem)', fontWeight: sl.descriptionWeight || 400, opacity: 0.9, marginBottom: 32, lineHeight: 1.7, maxWidth: isCenter ? 640 : undefined, margin: isCenter ? '0 auto 32px' : undefined, color: sl.descriptionColor || sl.textColor || '#fff' }}>{sl.description}</p>
            )}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: isCenter ? 'center' : isRight ? 'flex-end' : 'flex-start' }}>
              {sl.ctaText && sl.ctaUrl && (
                <a href={sl.ctaUrl} style={{ backgroundColor: sl.ctaBgColor || '#C8A951', color: sl.ctaTextColor || '#ffffff' }} className="px-8 py-3 font-semibold rounded-lg transition-opacity hover:opacity-80">
                  {sl.ctaText}
                </a>
              )}
              {sl.cta2Text && sl.cta2Url && (
                <a href={sl.cta2Url} style={{ backgroundColor: sl.cta2BgColor || 'transparent', color: sl.cta2TextColor || '#ffffff', border: '2px solid ' + (sl.cta2TextColor || 'rgba(255,255,255,0.6)') }} className="px-8 py-3 font-semibold rounded-lg transition-opacity hover:opacity-80">
                  {sl.cta2Text}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        {len > 1 && (
          <>
            <button onClick={() => setIdx(p => (p - 1 + len) % len)} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors" aria-label="Previous">‹</button>
            <button onClick={() => setIdx(p => (p + 1) % len)} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors" aria-label="Next">›</button>
          </>
        )}

        {/* Dots */}
        {len > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`w-3 h-3 rounded-full transition-all ${i === idx ? 'bg-[#C8A951] scale-110' : 'bg-white/50 hover:bg-white/80'}`} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Style helper ───
function wrapStyle(s) {
  if (!s) return {};
  return {
    paddingTop: s.paddingTop || 0,
    paddingBottom: s.paddingBottom || 0,
    paddingLeft: s.paddingLeft || 0,
    paddingRight: s.paddingRight || 0,
    marginTop: s.marginTop || 0,
    marginBottom: s.marginBottom || 0,
    backgroundColor: s.bgColor && s.bgColor !== '#ffffff' ? s.bgColor : undefined,
    color: s.textColor && s.textColor !== '#111827' ? s.textColor : undefined,
    borderRadius: s.borderRadius || 0,
    maxWidth: s.maxWidth && s.maxWidth !== 'full' ? s.maxWidth : undefined,
    margin: s.maxWidth && s.maxWidth !== 'full' ? '0 auto' : undefined,
  };
}

// ─── Widget router ───
function RenderWidget({ widget }) {
  const d = widget.data || {};
  const s = widget.styles || {};
  const map = {
    hero: HeroWidget, heading: HeadingWidget, text: TextWidget, image: ImageWidget,
    button: ButtonWidget, spacer: SpacerWidget, divider: DividerWidget, video: VideoWidget,
    cards: CardsWidget, cta: CTAWidget, icon_box: IconBoxWidget, testimonial: TestimonialWidget,
    counter: CounterWidget, accordion: AccordionWidget, html: HTMLWidget, slider: SliderWidget,
  };
  const Comp = map[widget.widget_type];
  if (!Comp) return null;

  const animClass = s.animation && s.animation !== 'none' ? `animate-${s.animation}` : '';
  const hoverClass = s.hoverEffect && s.hoverEffect !== 'none' ? `hover-${s.hoverEffect}` : '';
  const delay = s.animationDelay ? { animationDelay: `${s.animationDelay}ms` } : {};

  return (
    <div className={`${animClass} ${hoverClass}`.trim() || undefined} style={delay}>
      <Comp d={d} s={s} />
    </div>
  );
}

// ═══════════════════════════════════════════════════
// PageRenderer — main export
// ═══════════════════════════════════════════════════

export default function PageRenderer({ pageKey, insertAfter, replaceWidget, fallback }) {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWidgets = useCallback(() => {
    api.get(`/pages/${pageKey}/`)
      .then(res => setWidgets((res.data.results || res.data || []).sort((a, b) => a.order - b.order)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageKey]);

  useEffect(() => { fetchWidgets(); }, [fetchWidgets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-[#0B5D1E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (widgets.length === 0 && fallback) {
    return fallback;
  }

  return (
    <div>
      {widgets.map((w) => {
        const replacer = replaceWidget && (replaceWidget[w.label] || replaceWidget[w.widget_type]);
        return (
          <React.Fragment key={w.id}>
            {replacer ? replacer(w) : <RenderWidget widget={w} />}
            {insertAfter && insertAfter[w.widget_type]
              ? insertAfter[w.widget_type](w)
              : null}
            {insertAfter && insertAfter[w.label]
              ? insertAfter[w.label](w)
              : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export { RenderWidget };
